import React, { memo, useLayoutEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Task } from 'entities/task/model/types/tasks';
import { useClickOutside, useSetInputFocus, useToggleState } from 'shared/hooks';
import { TModalMode } from 'shared/model/types/modalMode.type';
import { Checkbox, Icon, Option, UiButton } from 'shared/ui';

import cls from './ObserversStaff.module.css';
import { ObserverItem } from './ui/ObserverItem';
import { useObserversStaff } from './useObserversStaff';

interface ObserversStaffProps {
    modalMode: TModalMode;
    text: string;
    highestRole: Task['context']['highest_role'];
    classes?: string;
    isDisabled?: boolean;
}

export const ObserversStaff = memo(
    ({ isDisabled = false, modalMode, text, highestRole, classes }: ObserversStaffProps) => {
        const {
            isOpen,
            optionFilter,
            filteredOptions,
            selectedStaff,
            handleOptionClick,
            handleInputChange,
            toggleDropdown,
            setIsOpen,
            employees,
        } = useObserversStaff(modalMode, isDisabled);
        const { t } = useTranslation();
        const [isClicked, setIsClicked] = useState(false);
        const [openedListStyle, setOpenedListStyle] = useState<React.CSSProperties>({});
        const [observersListStyle, setObserversListStyle] = useState<React.CSSProperties>({});
        const { rootRef } = useClickOutside({ isOpen, setIsOpen });
        const triggerRef = useRef<HTMLDivElement | null>(null);
        const inputRef = useSetInputFocus({ isOpen });
        const [isHovered, setIsHovered] = useToggleState();
        const { rootRef: clickedRef } = useClickOutside({
            isOpen: isClicked,
            setIsOpen: setIsClicked,
        });

        const handleClick = () => {
            if (highestRole != 'author') {
                // toggle();
                setIsClicked(!isClicked);
            }
        };

        useLayoutEffect(() => {
            const calcRect = () => {
                if (!isOpen) {
                    setOpenedListStyle({});
                    return;
                }

                if (!triggerRef.current) return;

                const rect = triggerRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const openUpward = spaceBelow < 170;

                setOpenedListStyle({
                    position: 'fixed',
                    top: openUpward ? rect.top - 8 : rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                    transform: openUpward ? 'translateY(-100%)' : 'none',
                });
            };

            calcRect();

            window.addEventListener('resize', calcRect);
            window.addEventListener('scroll', calcRect, true);

            return () => {
                window.removeEventListener('resize', calcRect);
                window.removeEventListener('scroll', calcRect, true);
            };
        }, [isOpen]);

        useLayoutEffect(() => {
            const calcRect = () => {
                if (!isClicked) {
                    setObserversListStyle({});
                    return;
                }

                if (!triggerRef.current) return;

                const rect = triggerRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const openUpward = spaceBelow < 170;

                setObserversListStyle({
                    position: 'fixed',
                    top: openUpward ? rect.top - 8 : rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                    transform: openUpward ? 'translateY(-100%)' : 'none',
                });
            };

            calcRect();

            window.addEventListener('resize', calcRect);
            window.addEventListener('scroll', calcRect, true);

            return () => {
                window.removeEventListener('resize', calcRect);
                window.removeEventListener('scroll', calcRect, true);
            };
        }, [isClicked]);

        return (
            <div
                className={cls.Container__Wrapper}
                onMouseEnter={setIsHovered}
                onMouseLeave={setIsHovered}
                ref={rootRef}
            >
                <div
                    ref={triggerRef}
                    className={classNames(cls.SearchStaffButton, [classes])}
                    onClick={toggleDropdown}
                    title={t('Features.ObserversStaff.AttributeTitle.text')}
                >
                    <div className={classNames(cls.Container, isDisabled && cls.disabled)}>
                        <span className={cls.staffIcon}>
                            <Icon iconName="taskWatchers" />
                        </span>
                        <ObserverItem
                            toggleDropdown={toggleDropdown}
                            selectedStaff={selectedStaff || []}
                            isDisabled={isDisabled}
                        />
                        {!selectedStaff.length && (
                            <UiButton
                                className={classNames(cls.Button, isDisabled && cls.disabled)}
                                onClick={toggleDropdown}
                                btnText={
                                    <span
                                        className={classNames(
                                            modalMode === 'addNewTask'
                                                ? cls.ButtonTextAddModal
                                                : cls.ButtonTextEditModal,
                                            isDisabled && cls.disabled
                                        )}
                                    >
                                        {text}
                                    </span>
                                }
                                startIcon={!isDisabled && <Icon iconName="plus" />}
                                variant="text"
                            />
                        )}
                    </div>
                    {isOpen && (
                        <div className={cls.OpenedListContainer} style={openedListStyle}>
                            <div className={cls.filterInput}>
                                <input
                                    ref={inputRef}
                                    className={cls.input}
                                    onChange={handleInputChange}
                                    value={optionFilter}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <ul
                                className={cls.optionsUl}
                                onMouseEnter={setIsHovered}
                                onMouseLeave={setIsHovered}
                            >
                                {optionFilter && optionFilter.length > 0 ? (
                                    <>
                                        {filteredOptions.map((option) => {
                                            const isSelectedOption = !!selectedStaff.filter(
                                                (item) => item.id === option.id
                                            ).length;
                                            return (
                                                <Option
                                                    key={option.id}
                                                    option={option}
                                                    onClick={handleOptionClick}
                                                    isDisabled={!option || option.id === null}
                                                    CheckedStatusAddon={
                                                        <Checkbox isActive={isSelectedOption} />
                                                    }
                                                    styles={cls.option}
                                                />
                                            );
                                        })}
                                    </>
                                ) : (
                                    <>
                                        {employees.map((option) => {
                                            const isSelectedOption = !!selectedStaff.filter(
                                                (item) => item.id === option.id
                                            ).length;
                                            return (
                                                <Option
                                                    key={option.id}
                                                    option={option}
                                                    onClick={handleOptionClick}
                                                    isDisabled={!option || option.id === null}
                                                    CheckedStatusAddon={
                                                        <Checkbox isActive={isSelectedOption} />
                                                    }
                                                    styles={cls.option}
                                                />
                                            );
                                        })}
                                    </>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                {isClicked && highestRole != 'author' && selectedStaff.length > 1 && (
                    <ul className={cls.positionObsList} ref={clickedRef} style={observersListStyle}>
                        {selectedStaff.map((obs) => (
                            <Option
                                key={obs.id}
                                option={obs}
                                onClick={() => {}}
                                isDisabled={!obs || obs.id === null}
                                styles={cls.option__addListItem}
                            />
                        ))}
                    </ul>
                )}
            </div>
        );
    }
);
